import type { RoundHistoryEntry } from '../types';
import styles from './RoundHistoryPanel.module.css';

export type HistoryRoundRow = {
  roundNumber: number;
  /** null / empty → "Picking strat…" for next rows */
  strategyName: string | null;
  side?: string | null;
};

export type RoundHistoryPanelProps = {
  completed: RoundHistoryEntry[];
  /** In-progress buy/active round — purple */
  current?: HistoryRoundRow | null;
  /** Upcoming pending round — grey, rendered above current */
  next?: HistoryRoundRow | null;
  /**
   * @deprecated use `current`
   */
  live?: HistoryRoundRow | null;
};

function sideClass(side: string): string {
  const s = side.toUpperCase();
  if (s === 'CT') return styles.sideCt;
  if (s === 'T') return styles.sideT;
  return '';
}

function isCompletedResult(result: RoundHistoryEntry['result']): boolean {
  return result === 'win' || result === 'loss';
}

export default function RoundHistoryPanel({
  completed,
  current,
  next,
  live,
}: RoundHistoryPanelProps) {
  const currentRow = current ?? live ?? null;
  const currentNum = currentRow != null ? Number(currentRow.roundNumber) : null;
  const nextNum = next != null ? Number(next.roundNumber) : null;

  // API round-history returns ALL match rounds (including pending). Only finished
  // rounds belong under the live row; pending next is shown above as `next`.
  const past = completed.filter((r) => {
    const n = Number(r.roundNumber);
    if (currentNum != null && n === currentNum) return false;
    if (nextNum != null && n === nextNum) return false;
    if (currentNum != null && n > currentNum) return false;
    if (!isCompletedResult(r.result)) return false;
    return true;
  });
  const ordered = [...past].sort((a, b) => Number(b.roundNumber) - Number(a.roundNumber));

  const topNumber = next?.roundNumber ?? currentRow?.roundNumber ?? ordered[0]?.roundNumber;
  const bottomNumber =
    ordered.length > 0
      ? ordered[ordered.length - 1].roundNumber
      : currentRow?.roundNumber ?? next?.roundNumber;
  const meta =
    topNumber != null && bottomNumber != null
      ? topNumber === bottomNumber
        ? `R${topNumber}`
        : `R${bottomNumber} – R${topNumber}`
      : '—';

  const empty = !currentRow && !next && ordered.length === 0;

  return (
    <section className={styles.panel} aria-labelledby="live-history-heading">
      <div className={styles.head}>
        <h2 id="live-history-heading">Round History</h2>
        <span className={styles.meta}>{meta}</span>
      </div>
      {empty ? (
        <p className={styles.empty}>No completed rounds yet.</p>
      ) : (
        <ul className={styles.list}>
          {next && (
            <li className={`${styles.row} ${styles.pending}`}>
              <span className={styles.rn}>R{next.roundNumber}</span>
              <span className={`${styles.outcome} ${styles.pendingPip}`} aria-label="Pending">
                ○
              </span>
              <span className={`${styles.strat} ${!next.strategyName ? styles.picking : ''}`}>
                {next.strategyName?.trim() ? next.strategyName : 'Picking strat…'}
              </span>
              {next.side ? (
                <span className={`${styles.side} ${sideClass(next.side)}`}>{next.side}</span>
              ) : (
                <span className={styles.sidePlaceholder} />
              )}
            </li>
          )}
          {currentRow && (
            <li className={`${styles.row} ${styles.now}`}>
              <span className={styles.rn}>R{currentRow.roundNumber}</span>
              <span className={`${styles.outcome} ${styles.live}`} aria-label="Live">
                ●
              </span>
              <span className={styles.strat}>{currentRow.strategyName?.trim() || '—'}</span>
              <span className={`${styles.side} ${sideClass(currentRow.side ?? '')}`}>
                {currentRow.side || '—'}
              </span>
            </li>
          )}
          {ordered.map((r) => (
            <li key={r.roundNumber} className={styles.row}>
              <span className={styles.rn}>R{r.roundNumber}</span>
              <span
                className={`${styles.outcome} ${r.result === 'win' ? styles.w : r.result === 'loss' ? styles.l : ''}`}
                aria-label={r.result ?? 'unknown'}
              >
                {r.result === 'win' ? 'W' : r.result === 'loss' ? 'L' : '·'}
              </span>
              <span className={styles.strat}>{r.strategyName || '—'}</span>
              <span className={`${styles.side} ${sideClass(r.side)}`}>{r.side || '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
